/**
 * WebSocket Client for Jester Chess
 * Handles connection and communication with game server
 */

import { io, Socket } from 'socket.io-client';
import type {
  GameState,
  Color,
  DiceRoll,
  WSMessageType,
  ChessMove,
} from '@jester-chess/shared';

export class ClientSocket {
  private socket: Socket;
  private gameStateCallbacks: Array<(state: GameState) => void> = [];
  private gameStartCallbacks: Array<(state: GameState, color: Color) => void> = [];
  private waitingCallbacks: Array<() => void> = [];
  private reconnectCallbacks: Array<(state: GameState, color: Color) => void> = [];
  private gameOverCallbacks: Array<(winner: Color | null, state: GameState) => void> = [];
  private playerId: string | null = null;
  private gameId: string | null = null;

  constructor() {
    // In production, if served from same origin, use empty string (same origin)
    // Otherwise use VITE_SERVER_URL or fallback to localhost for development
    const serverUrl = import.meta.env.VITE_SERVER_URL || 
      (import.meta.env.PROD ? '' : 'http://localhost:3000');
    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    this.socket.on('connect', () => {
      console.log('Connected to game server');
      
      // Try to reconnect if we have a player ID
      if (this.playerId) {
        this.socket.emit('reconnect_game', { playerId: this.playerId });
      } else if (this.gameId) {
        // Join specific game by ID
        this.socket.emit('join_game', { gameId: this.gameId });
      } else {
        // Automatically join game when connected (legacy URL-based matching)
        this.socket.emit('join_game');
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from game server');
    });

    this.socket.on('message', (message: WSMessageType) => {
      this.handleMessage(message);
    });

    this.socket.on('error', (error: { message: string }) => {
      console.error('Socket error:', error);
    });
  }

  private handleMessage(message: WSMessageType): void {
    switch (message.type) {
      case 'waiting_for_player':
        this.waitingCallbacks.forEach((cb) => cb());
        break;

      case 'game_start':
        const { gameState, yourColor } = message.payload as {
          gameState: GameState;
          yourColor: Color;
        };
        // Store player ID from game state
        this.playerId = yourColor === 'white' ? gameState.whitePlayer : gameState.blackPlayer;
        this.gameStartCallbacks.forEach((cb) => cb(gameState, yourColor));
        break;

      case 'reconnect':
        const reconnectData = message.payload as {
          gameState: GameState;
          yourColor: Color;
          playerId: string;
        };
        this.playerId = reconnectData.playerId;
        this.reconnectCallbacks.forEach((cb) => cb(reconnectData.gameState, reconnectData.yourColor));
        break;

      case 'move':
        const { gameState: updatedState } = message.payload as {
          move: ChessMove;
          gameState: GameState;
        };
        this.gameStateCallbacks.forEach((cb) => cb(updatedState));
        break;

      case 'jester_move':
        const jesterMoveData = message.payload as {
          diceRoll: DiceRoll;
          result: unknown;
          gameState: GameState;
        };
        this.gameStateCallbacks.forEach((cb) => cb(jesterMoveData.gameState));
        break;

      case 'game_over':
        const gameOverData = message.payload as {
          winner: Color | null;
          gameState: GameState;
        };
        this.gameOverCallbacks.forEach((cb) => cb(gameOverData.winner, gameOverData.gameState));
        break;

      case 'game_state_update':
        const stateUpdate = message.payload as {
          gameState: GameState;
        };
        this.gameStateCallbacks.forEach((cb) => cb(stateUpdate.gameState));
        break;

      case 'jester_move_skipped':
        const skippedData = message.payload as {
          gameState: GameState;
        };
        this.gameStateCallbacks.forEach((cb) => cb(skippedData.gameState));
        break;

      case 'error':
        const { message: errorMsg } = message.payload as { message: string };
        console.error('Game error:', errorMsg);
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  }

  public onGameStart(callback: (state: GameState, color: Color) => void): void {
    this.gameStartCallbacks.push(callback);
  }

  public onGameStateUpdate(callback: (state: GameState) => void): void {
    this.gameStateCallbacks.push(callback);
  }

  public onWaiting(callback: () => void): void {
    this.waitingCallbacks.push(callback);
  }

  public onReconnect(callback: (state: GameState, color: Color) => void): void {
    this.reconnectCallbacks.push(callback);
  }

  public onGameOver(callback: (winner: Color | null, state: GameState) => void): void {
    this.gameOverCallbacks.push(callback);
  }

  public requestGameState(): void {
    this.socket.emit('request_game_state');
  }

  public getPlayerId(): string | null {
    return this.playerId;
  }

  public sendChessMove(move: ChessMove): void {
    this.socket.emit('chess_move', move);
  }

  public sendJesterRoll(diceRoll: DiceRoll): void {
    this.socket.emit('jester_roll', diceRoll);
  }

  public sendSkipJesterMove(): void {
    this.socket.emit('skip_jester_move');
  }

  public sendResign(): void {
    this.socket.emit('resign');
  }

  public connect(): void {
    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  public disconnect(): void {
    this.socket.disconnect();
  }

  public setGameId(gameId: string): void {
    this.gameId = gameId;
  }
}

